
import React, { useRef } from 'react';
import { AgentProfile, Language, Dialect, Attachment } from '../../types';
import { TRANSLATIONS, DIALECTS, VOICES } from '../../constants';

interface RobotOptionsProps {
  language: Language;
  activeAgent: AgentProfile;
  activeTab: 'agents' | 'visual' | 'options';
  hasApiAccess: boolean;
  onUpdateAgent: (updates: Partial<AgentProfile>) => void;
  onCopyApiKey: () => void;
  isRtl: boolean;
}

const RobotOptions: React.FC<RobotOptionsProps> = ({ 
  language, activeAgent, activeTab, hasApiAccess, 
  onUpdateAgent, onCopyApiKey, isRtl 
}) => {
  const t = TRANSLATIONS[language];
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newFiles: Attachment[] = [];
    // Fix: Explicitly cast Array.from(files) to File[] to avoid 'unknown' type errors
    (Array.from(files) as File[]).forEach(file => {
        const reader = new FileReader();
        reader.onload = (ev) => {
            newFiles.push({
                // Fix: Access properties of the correctly typed File object
                name: file.name,
                type: file.type,
                data: ev.target?.result as string,
                url: URL.createObjectURL(file)
            });
            if (newFiles.length === files.length) {
                const currentFiles = activeAgent.trainingFiles || [];
                onUpdateAgent({ trainingFiles: [...currentFiles, ...newFiles] });
            }
        };
        // Fix: Pass the typed File object to readAsDataURL which expects a Blob
        reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeFile = (index: number) => {
      const currentFiles = activeAgent.trainingFiles || [];
      const updated = currentFiles.filter((_, i) => i !== index);
      onUpdateAgent({ trainingFiles: updated });
  };

  return (
    <aside className={`lg:w-[450px] w-full bg-white dark:bg-[#0f0f13] border-t lg:border-t-0 lg:border-l border-slate-200 dark:border-white/5 flex flex-col absolute lg:static inset-0 lg:inset-auto z-50 transition-transform duration-300 ${activeTab === 'options' ? 'translate-x-0' : (isRtl ? '-translate-x-full lg:translate-x-0' : 'translate-x-full lg:translate-x-0')}`}>
       <div className="h-16 lg:h-20 border-b border-slate-200 dark:border-white/5 flex items-center justify-between px-6 lg:px-8 bg-slate-50 dark:bg-black/20">
          <div><h3 className="text-xs lg:text-base font-black uppercase text-slate-800 dark:text-white tracking-wider">{isRtl ? 'إعدادات الوكيل' : 'Agent Configuration'}</h3></div>
          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-500"><i className="fas fa-sliders"></i></div>
       </div>
       <div className="flex-1 overflow-y-auto custom-scrollbar p-6 lg:p-8 space-y-8">
          <div className="space-y-4">
             <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Identity</h4>
             <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-white/5 flex items-center justify-center text-2xl text-slate-400"><i className={`fas ${activeAgent.avatar}`}></i></div>
                <div className="flex-1 space-y-2">
                   <input type="text" value={activeAgent.name} onChange={(e) => onUpdateAgent({ name: e.target.value })} className="w-full bg-slate-50 dark:bg-black/20 border border-slate-200 dark:border-white/10 rounded-xl px-3 py-2 text-sm font-bold dark:text-white outline-none focus:border-emerald-500" placeholder="Name" />
                   <input type="text" value={activeAgent.role} onChange={(e) => onUpdateAgent({ role: e.target.value })} className="w-full bg-slate-50 dark:bg-black/20 border border-slate-200 dark:border-white/10 rounded-xl px-3 py-2 text-sm dark:text-white outline-none focus:border-emerald-500" placeholder="Role" />
                </div>
             </div>
          </div>
          <div className="space-y-4">
             <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Behavior</h4>
             <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><label className="text-[9px] font-bold text-slate-500">Dialect</label><select value={activeAgent.dialect} onChange={(e) => onUpdateAgent({ dialect: e.target.value as Dialect })} className="w-full bg-slate-50 dark:bg-black/20 border-none rounded-xl px-3 py-2 text-xs font-bold dark:text-white">{DIALECTS.map(d => <option key={d.id} value={d.id}>{d.name[language]}</option>)}</select></div>
                <div className="space-y-1"><label className="text-[9px] font-bold text-slate-500">Voice</label><select value={activeAgent.voice} onChange={(e) => onUpdateAgent({ voice: e.target.value })} className="w-full bg-slate-50 dark:bg-black/20 border-none rounded-xl px-3 py-2 text-xs font-bold dark:text-white">{VOICES.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}</select></div>
             </div>
             <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-white/5 rounded-xl">
                <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{t.autoReply}</span>
                <input type="checkbox" checked={activeAgent.autoReply} onChange={(e) => onUpdateAgent({ autoReply: e.target.checked })} className="toggle" />
             </div>
             <textarea value={activeAgent.systemPrompt} onChange={(e) => onUpdateAgent({ systemPrompt: e.target.value })} className="w-full h-24 bg-slate-50 dark:bg-black/20 border border-slate-200 dark:border-white/10 rounded-xl p-3 text-xs dark:text-white outline-none focus:border-emerald-500 resize-none" placeholder={t.visionPrompt} />
          </div>

          <div className="space-y-4">
             <div className="flex items-center justify-between">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t.knowledgeBase}</h4>
                {hasApiAccess && <span className="bg-emerald-500/10 text-emerald-500 text-[9px] font-black px-2 py-0.5 rounded">PRO</span>}
             </div>
             
             {hasApiAccess ? (
                <div className="space-y-3">
                    <div onClick={() => fileInputRef.current?.click()} className="border-2 border-dashed border-slate-200 dark:border-white/10 rounded-xl p-6 flex flex-col items-center justify-center cursor-pointer hover:border-emerald-500/50 hover:bg-slate-50 dark:hover:bg-white/5 transition-all group">
                        <input type="file" ref={fileInputRef} onChange={handleFileUpload} multiple className="hidden" accept=".pdf,.txt,.docx,.csv" />
                        <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-white/5 flex items-center justify-center text-slate-400 group-hover:text-emerald-500 mb-2 transition-colors">
                            <i className="fas fa-cloud-arrow-up"></i>
                        </div>
                        <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400">{isRtl ? 'اضغط لرفع ملفات التدريب' : 'Click to upload training files'}</p>
                        <p className="text-[9px] text-slate-400 mt-1">PDF, TXT, DOCX</p>
                    </div>
                    
                    {activeAgent.trainingFiles && activeAgent.trainingFiles.length > 0 && (
                        <div className="space-y-2">
                            {activeAgent.trainingFiles.map((file, idx) => (
                                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-white/5 rounded-xl border border-slate-100 dark:border-white/5 group">
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500 shrink-0">
                                            <i className="fas fa-file-lines text-xs"></i>
                                        </div>
                                        <span className="text-[10px] font-bold dark:text-white truncate">{file.name}</span>
                                    </div>
                                    <button onClick={() => removeFile(idx)} className="text-slate-400 hover:text-rose-500 transition-colors p-1"><i className="fas fa-times"></i></button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
             ) : (
                <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 text-center">
                    <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-500 mx-auto mb-2"><i className="fas fa-lock"></i></div>
                    <p className="text-[10px] font-bold text-amber-600 dark:text-amber-400 mb-2">{isRtl ? 'ميزة للمشتركين فقط' : 'Premium Feature Only'}</p>
                    <p className="text-[9px] text-amber-600/70 dark:text-amber-400/70">{isRtl ? 'قم بترقية باقتك لرفع ملفات وتدريب الوكيل عليها.' : 'Upgrade your plan to upload files and train your agent.'}</p>
                </div>
             )}
          </div>

          {hasApiAccess && activeAgent.apiKey && (
             <div className="bg-slate-900 rounded-xl p-4 border border-slate-800 relative group">
                <button onClick={onCopyApiKey} className="absolute top-2 right-2 p-1.5 bg-white/10 rounded hover:bg-white/20 text-white"><i className="fas fa-copy text-xs"></i></button>
                <label className="text-[9px] font-bold text-slate-500 uppercase block mb-1">Secret Key</label>
                <p className="font-mono text-xs text-emerald-400 break-all">{activeAgent.apiKey}</p>
             </div>
          )}
       </div>
    </aside>
  );
};
export default RobotOptions;
